import { createAction } from 'redux-starter-kit';
import { notification } from 'antd';
import { get, post } from '../../../../utility/via-fetch';
import { putUser } from '../../../domain/users/userActions';
import { requestStart, requestSucceded, requestFailed } from '../../../domain/requests/RequestActions';
import { putEmailContent } from '../../../domain/emails/emailActions';
import { addNotification, addMessage } from '../../application/messageUiActions';
import { setSessionUser } from '../../application/sessionUiActions';
import routeLocations, { configRoutes } from '../../../../utility/route-locations';

/*
 * An action that signifies a request to verify the users email-address
 */
export const verifyUserEmail = createAction('VERIFY_USER_EMAIL');


/*
 * A action that send and email-verification request token to the server to check prove the users email is verfied
 * @param {string} requestToken should be /verify-email/{HaxadecimalToken}
 * @param {intl} intl Should be the intl currenly used by the application
 */
export function requestVerifyEmail(requestToken, showResendEmailNotification) {
  return async (dispatch) => {
    const requestId = dispatch(requestStart(verifyUserEmail.toString()));
    dispatch(verifyUserEmail(requestId));

    try {
      const user = await post(`/api/users${requestToken}`, undefined, { });
      dispatch(putUser(user)); // override the user with our new 'verified' user
      dispatch(addNotification('success', null, 'emailVerifySuccess', [], 'emailVerifySuccessDesc', []));
      dispatch(requestSucceded(requestId));
    } catch (error) {
      dispatch(requestFailed(requestId)); // We failed.
      showResendEmailNotification();
    }
  };
}

/**
 * An action that signifies a request for the server to send an email verification email
 */
export const sendVerificationEmail = createAction('SEND_VERIFICATION_EMAIL');

/*
 * A action that send and email-verification request token to the server to check prove the users email is verfied
 * @param {string} requestToken should be /verify-email/{HaxadecimalToken}
 * @param {intl} intl Should be the intl currenly used by the application
 */
export function requestSendVerificationEmail() {
  return async (dispatch) => {
    const requestId = dispatch(requestStart(sendVerificationEmail.toString()));
    dispatch(sendVerificationEmail(requestId));
    try {
      await post('/api/users/verify-email', undefined, { });
      dispatch(addMessage('success', 'verifyEmailSuccess'));
      dispatch(requestSucceded(requestId));
    } catch (error) {
      dispatch(requestFailed(requestId)); // We failed.
      dispatch(addMessage('error', 'emailFail'));
    }
    notification.close('resend-verification-email');
  };
}

/**
 * An action that signifies a request for the server to send a change email email
 */
export const sendChangeEmailEmail = createAction('SEND_CHANGE_EMAIL_EMAIL');

/*
 * A action that send and email-verification request token to the server to check prove the users email is verfied
 * @param {string} requestToken should be /verify-email/{HaxadecimalToken}
 * @param {intl} intl Should be the intl currenly used by the application
 */
export function requestSendChangeEmailEmail(newEmail) {
  return async (dispatch) => {
    const requestId = dispatch(requestStart(sendChangeEmailEmail.toString()));
    dispatch(sendChangeEmailEmail(requestId));
    try {
      await post('/api/users/change-email', undefined, { value: newEmail });
      dispatch(addMessage('success', 'verifyEmailSuccess'));
      dispatch(requestSucceded(requestId, newEmail));
    } catch (error) {
      dispatch(requestFailed(requestId, error)); // We failed.
    }
  };
}

/*
 * An action that signifies a request to change a users email
 */
export const changeEmail = createAction('CHANGE_EMAIL');


/**
 * A action that sends a change-email token to the server to confirma a change in email address for the user
 * @param {string} requestToken should be /reset-password/{HrxadecimalToken}
 * @param {history} history The history form the router to allow loading a new page on success
 */
export function requestChangeEmail(requestToken, username, password, history) {
  return async (dispatch) => {
    const requestId = dispatch(requestStart(changeEmail.toString()));
    dispatch(changeEmail(requestId));
    try {
      const user = await post(`/api/users/change-email${requestToken}`, undefined, { username, password });
      dispatch(addMessage('success', 'emailSaved'));
      // sign them in.
      dispatch(setSessionUser(user.userId));
      dispatch(putUser(user));
      history.push(routeLocations.dashboard);
      dispatch(requestSucceded(requestId));
    } catch (error) {
      if (error.code === 401) {
        dispatch(addMessage('error', 'loginFailed'));
      }
      dispatch(requestFailed(requestId, error)); // We failed.
    }
  };
}

/*
 * An action that signifies a request to retrieve a users currently pending email change address
 */
export const fetchEmailSentTo = createAction('FETCH_EMAIL_SENT_TO');


/**
 * An action that retrieves a users currently pending email change address
 */
export function requestEmailSentTo() {
  return async (dispatch) => {
    const requestId = dispatch(requestStart(fetchEmailSentTo.toString()));
    dispatch(fetchEmailSentTo(requestId));
    try {
      const emailSentTo = await get('/api/users/pending-request', undefined, {});
      dispatch(requestSucceded(requestId, emailSentTo.value));
    } catch (error) {
      dispatch(requestFailed(requestId, error)); // We failed.
    }
  };
}

/*
 * An action that signifies a request to retrieve all email content currently pending email change address
 */
export const saveEmailContent = createAction('SAVE_EMAIL_CONTENT');


/**
 * An action that retrieves a users currently pending email change address
 */
export function requestSaveEmailContent(emailContent, history) {
  return async (dispatch) => {
    const requestId = dispatch(requestStart(saveEmailContent.toString()));
    dispatch(saveEmailContent(requestId));
    try {
      await post(`/api/email-content/${emailContent.emailType}`, undefined, { ...emailContent });
      // Server throws a validation exception if it is given invalid html so no need to re-load it
      dispatch(putEmailContent(emailContent));
      history.push(configRoutes.configure_email_content);
      dispatch(requestSucceded(requestId));
    } catch (error) {
      dispatch(requestFailed(requestId, error)); // We failed.
    }
  };
}


/*
 * An action that signifies a request to retrieve all email content currently pending email change address
 */
export const fetchSpecificEmailContent = createAction('FETCH_EMAIL_CONTENT');


/**
 * An action that retrieves a users currently pending email change address
 */
export function requestSpecificEmailContent(emailType) {
  return async (dispatch) => {
    const requestId = dispatch(requestStart(fetchSpecificEmailContent.toString()));
    dispatch(fetchSpecificEmailContent(requestId));
    try {
      const emailContent = await get(`/api/email-content/${emailType}`, undefined, {});
      dispatch(putEmailContent(emailContent));
      dispatch(requestSucceded(requestId));
    } catch (error) {
      dispatch(requestFailed(requestId, error)); // We failed.
    }
  };
}
